import type { CameraSite, LatLng, Route } from "./types";
import { distanceMeters, type NavigationProgress } from "./navigation";

export interface CameraAlert {
  camera: CameraSite;
  /** Distance along the remaining route, not straight-line distance. */
  distanceMeters: number;
  message: string;
}

const LOOKAHEAD_METERS = 600;
const CORRIDOR_METERS = 40;
const MIN_AHEAD_METERS = 8;
const MAX_HEADING_DIFFERENCE = 55;
const EARTH_RADIUS = 6_371_008.8;
const radians = (degrees: number) => (degrees * Math.PI) / 180;
const longitudeDelta = (a: number, b: number) => ((b - a + 540) % 360) - 180;
const validPoint = (point: LatLng) =>
  Number.isFinite(point.lat) &&
  Number.isFinite(point.lng) &&
  Math.abs(point.lat) <= 90 &&
  Math.abs(point.lng) <= 180;

interface Segment {
  a: LatLng;
  b: LatLng;
  start: number;
  end: number;
  bearing: number;
}
interface Corridor {
  segments: Segment[];
  total: number;
}
// Route values are immutable API results. A new route gets a new corridor;
// ordinary GPS ticks reuse the cumulative lengths and travel bearings.
const routeCorridors = new WeakMap<Route, Corridor | null>();

function corridor(route: Route): Corridor | null {
  const cached = routeCorridors.get(route);
  if (cached !== undefined) return cached;
  const points = route.geometry.map((point) => ({
    lat: point[1],
    lng: point[0],
  }));
  if (points.length < 2 || !points.every(validPoint)) {
    routeCorridors.set(route, null);
    return null;
  }
  const segments: Segment[] = [];
  let total = 0;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1],
      b = points[i];
    const length = distanceMeters(a, b);
    if (length <= 0) continue;
    const delta = radians(longitudeDelta(a.lng, b.lng));
    const bearing =
      ((Math.atan2(
        Math.sin(delta) * Math.cos(radians(b.lat)),
        Math.cos(radians(a.lat)) * Math.sin(radians(b.lat)) -
          Math.sin(radians(a.lat)) * Math.cos(radians(b.lat)) * Math.cos(delta),
      ) *
        180) /
        Math.PI +
        360) %
      360;
    segments.push({ a, b, start: total, end: total + length, bearing });
    total += length;
  }
  const result = total > 0 ? { segments, total } : null;
  routeCorridors.set(route, result);
  return result;
}

function projection(camera: CameraSite, segment: Segment) {
  const { a, b } = segment;
  const scale =
    EARTH_RADIUS * Math.cos(radians((a.lat + b.lat + camera.lat) / 3));
  const bx = radians(longitudeDelta(a.lng, b.lng)) * scale;
  const by = radians(b.lat - a.lat) * EARTH_RADIUS;
  const px = radians(longitudeDelta(a.lng, camera.lng)) * scale;
  const py = radians(camera.lat - a.lat) * EARTH_RADIUS;
  const denominator = bx * bx + by * by;
  if (denominator <= 0) return null;
  const fraction = (px * bx + py * by) / denominator;
  // Do not snap a camera behind/beyond a road segment onto its endpoint.
  // Adjacent segments can still match a camera at an actual route vertex.
  if (fraction < -1e-9 || fraction > 1 + 1e-9) return null;
  const bounded = Math.max(0, Math.min(1, fraction));
  return {
    lateral: Math.hypot(px - bx * bounded, py - by * bounded),
    along: segment.start + (segment.end - segment.start) * bounded,
  };
}

function matchesHeading(camera: CameraSite, bearing: number): boolean {
  return [camera.heading, camera.secondaryHeading].some(
    (heading) =>
      heading !== null &&
      Number.isFinite(heading) &&
      heading >= 0 &&
      heading <= 360 &&
      Math.abs(((heading - bearing + 540) % 360) - 180) <=
        MAX_HEADING_DIFFERENCE,
  );
}

export function cameraLabel(
  camera: Pick<CameraSite, "kind" | "status">,
): string {
  if (camera.kind === "AVERAGE_SPEED" && camera.status === "EXPERIMENTAL")
    return "מדידת מהירות ממוצעת ניסיונית";
  const labels: Record<CameraSite["kind"], string> = {
    SPEED: "מצלמת מהירות",
    RED_LIGHT_SPEED: "מצלמת רמזור ומהירות",
    AVERAGE_SPEED: "מדידת מהירות ממוצעת",
    RAIL_CROSSING: "מצלמה במפגש מסילת ברזל",
    BUS_LANE: "מצלמת נתיב תחבורה ציבורית",
    SECURITY: "מצלמת אבטחה",
    DUMMY: "מצלמת דמה",
    OTHER: "מצלמה",
  };
  return labels[camera.kind];
}

/**
 * Read-only source-camera alerts, separate from community reports. The caller
 * gates live navigation and GPS freshness and manages seenIds per trip.
 * Missing headings suppress spoken alerts: proximity alone cannot distinguish
 * opposite carriageways. Heading means monitored traffic direction, not lens direction.
 * Source status is not a guarantee of current enforcement; no speed limit is spoken.
 */
export function nextCameraAlert(
  cameras: readonly CameraSite[],
  route: Route,
  position: LatLng,
  progress: NavigationProgress,
  seenIds: ReadonlySet<string>,
): CameraAlert | null {
  if (
    progress.offRoute ||
    progress.arrived ||
    !validPoint(position) ||
    !Number.isFinite(progress.progress) ||
    progress.progress < 0 ||
    progress.progress >= 1
  )
    return null;
  const path = corridor(route);
  if (!path) return null;
  const current = progress.progress * path.total;
  const limit = current + LOOKAHEAD_METERS;
  // Binary-search the current segment; never scan the full route for each camera.
  let low = 0,
    high = path.segments.length;
  while (low < high) {
    const mid = (low + high) >>> 1;
    if (path.segments[mid].end <= current) low = mid + 1;
    else high = mid;
  }
  const upcoming: Segment[] = [];
  for (
    let i = low;
    i < path.segments.length && path.segments[i].start <= limit;
    i++
  )
    upcoming.push(path.segments[i]);
  let best: CameraAlert | null = null;
  for (const camera of cameras) {
    if (
      seenIds.has(camera.id) ||
      !validPoint(camera) ||
      camera.kind === "SECURITY" ||
      camera.kind === "DUMMY" ||
      !(
        camera.status === "POTENTIALLY_ACTIVE" ||
        (camera.status === "EXPERIMENTAL" && camera.kind === "AVERAGE_SPEED")
      ) ||
      distanceMeters(position, camera) > 800
    )
      continue;
    for (const segment of upcoming) {
      if (!matchesHeading(camera, segment.bearing)) continue;
      const projected = projection(camera, segment);
      if (!projected || projected.lateral > CORRIDOR_METERS) continue;
      const ahead = projected.along - current;
      if (ahead <= MIN_AHEAD_METERS || ahead > LOOKAHEAD_METERS) continue;
      if (
        !best ||
        ahead < best.distanceMeters ||
        (ahead === best.distanceMeters && camera.id < best.camera.id)
      ) {
        best = {
          camera,
          distanceMeters: ahead,
          message: `${cameraLabel(camera)} בהמשך המסלול, לפי המאגר`,
        };
      }
      // Segments are ordered: this is this camera's nearest future occurrence.
      break;
    }
  }
  return best;
}
